import { useEffect, useMemo, useState } from "react";
import { supabase } from "@/integrations/supabase/client";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Loader2, CreditCard, CheckCircle2, AlertCircle } from "lucide-react";
import { toast } from "sonner";

interface MemberRow {
  id: string;
  name: string | null;
  email: string | null;
  chapter: string | null;
  joined_date: string | null;
  next_renewal_date: string | null;
  membership_status: string | null;
  renewal_source: string | null;
}

interface PaymentRow {
  id: string;
  status: string;
  mode: string;
  amount: number;
  paid_at: string | null;
  created_at: string;
}

const statusLabel: Record<string, { label: string; variant: "default" | "secondary" | "destructive" | "outline" }> = {
  good_standing: { label: "Good standing", variant: "default" },
  grace: { label: "Grace period", variant: "secondary" },
  lapsed: { label: "Lapsed", variant: "destructive" },
  exempt: { label: "Exempt", variant: "outline" },
};

const RenewalTab = ({ userId }: { userId?: string }) => {
  const [member, setMember] = useState<MemberRow | null>(null);
  const [payments, setPayments] = useState<PaymentRow[]>([]);
  const [loading, setLoading] = useState(true);
  const [paying, setPaying] = useState(false);

  const load = async () => {
    if (!userId) return;
    setLoading(true);
    try {
      const { data: mem, error: memErr } = await supabase
        .from("members")
        .select(
          "id, name, email, chapter, joined_date, next_renewal_date, membership_status, renewal_source"
        )
        .eq("user_id", userId)
        .maybeSingle();
      if (memErr) throw memErr;
      setMember(mem as MemberRow | null);
      if (mem?.id) {
        const { data: pays } = await supabase
          .from("payments")
          .select("id, status, mode, amount, paid_at, created_at")
          .eq("member_id", mem.id)
          .neq("status", "superseded")
          .order("created_at", { ascending: false });
        setPayments((pays as PaymentRow[]) || []);
      }
    } catch (e: any) {
      toast.error(e?.message || "Failed to load renewal info");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    load();
  }, [userId]);

  const handleRenew = async () => {
    if (!member?.id) return;
    setPaying(true);
    try {
      const { data, error } = await supabase.functions.invoke("create-membership-checkout", {
        body: {
          member_id: member.id,
          mode: "renewal",
          success_url: `${window.location.origin}/portal`,
          cancel_url: `${window.location.origin}/portal`,
        },
      });
      if (error) throw error;
      if ((data as any)?.error) throw new Error((data as any).error);
      const url = (data as any)?.url;
      if (url) window.location.href = url;
      else throw new Error("No checkout URL returned");
    } catch (e: any) {
      toast.error(e?.message || "Failed to start checkout");
      setPaying(false);
    }
  };

  const showRenewButton = useMemo(() => {
    if (!member?.next_renewal_date) return false;
    if (member.membership_status === "exempt") return false;
    const renewal = new Date(member.next_renewal_date + "T00:00:00Z");
    const today = new Date();
    const daysUntil = Math.ceil((renewal.getTime() - today.getTime()) / (1000 * 60 * 60 * 24));
    return daysUntil <= 60 || member.membership_status === "grace" || member.membership_status === "lapsed";
  }, [member]);

  if (loading) {
    return (
      <div className="flex items-center justify-center py-12">
        <Loader2 className="h-6 w-6 animate-spin text-muted-foreground" />
      </div>
    );
  }

  if (!member) {
    return (
      <Card>
        <CardContent className="py-10 text-center text-muted-foreground">
          No member profile linked to this account. Contact an admin if you believe this is an error.
        </CardContent>
      </Card>
    );
  }

  const status = member.membership_status || "good_standing";
  const statusInfo = statusLabel[status] || { label: status, variant: "outline" };

  return (
    <div className="space-y-6">
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <CreditCard className="h-5 w-5" />
            Membership renewal
          </CardTitle>
          <CardDescription>Review your membership status and renew your annual dues.</CardDescription>
        </CardHeader>
        <CardContent className="space-y-6">
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
            <div className="space-y-1">
              <div className="text-sm text-muted-foreground">Status</div>
              <div>
                <Badge variant={statusInfo.variant}>{statusInfo.label}</Badge>
              </div>
            </div>
            <div className="space-y-1">
              <div className="text-sm text-muted-foreground">Next renewal date</div>
              <div className="font-medium">
                {member.next_renewal_date
                  ? new Date(member.next_renewal_date + "T00:00:00Z").toLocaleDateString("en-US", {
                      month: "long",
                      day: "numeric",
                      year: "numeric",
                      timeZone: "UTC",
                    })
                  : "—"}
              </div>
            </div>
            <div className="space-y-1">
              <div className="text-sm text-muted-foreground">Chapter</div>
              <div className="font-medium capitalize">{member.chapter || "—"}</div>
            </div>
            <div className="space-y-1">
              <div className="text-sm text-muted-foreground">Renewal method</div>
              <div className="font-medium">
                {member.renewal_source === "stripe" ? "Stripe auto-renewal" : "Manual"}
              </div>
            </div>
          </div>

          {showRenewButton && (
            <div className="rounded-lg border bg-muted/40 p-4">
              <div className="flex flex-col sm:flex-row items-start sm:items-center justify-between gap-4">
                <div className="space-y-1">
                  <div className="font-medium flex items-center gap-2">
                    <AlertCircle className="h-4 w-4" />
                    Annual membership fee: $300/year
                  </div>
                  <div className="text-sm text-muted-foreground">
                    Renew now to keep your membership in good standing.
                  </div>
                </div>
                <Button onClick={handleRenew} disabled={paying} className="shrink-0">
                  {paying ? <Loader2 className="h-4 w-4 animate-spin mr-2" /> : <CreditCard className="h-4 w-4 mr-2" />}
                  Pay $300 renewal
                </Button>
              </div>
            </div>
          )}

          {!showRenewButton && status !== "exempt" && (
            <div className="flex items-center gap-2 text-sm text-muted-foreground">
              <CheckCircle2 className="h-4 w-4" />
              Your membership is current. The renewal button will appear 60 days before your next renewal date.
            </div>
          )}
        </CardContent>
      </Card>

      {payments.length > 0 && (
        <Card>
          <CardHeader>
            <CardTitle>Payment history</CardTitle>
          </CardHeader>
          <CardContent>
            <div className="space-y-3">
              {payments.map((p) => (
                <div
                  key={p.id}
                  className="flex items-center justify-between rounded-lg border p-3 text-sm"
                >
                  <div>
                    <div className="font-medium capitalize">
                      {p.mode === "new_member" ? "New membership" : "Renewal"}
                    </div>
                    <div className="text-muted-foreground">
                      {p.paid_at
                        ? new Date(p.paid_at).toLocaleDateString("en-US")
                        : new Date(p.created_at).toLocaleDateString("en-US")}
                    </div>
                  </div>
                  <div className="text-right">
                    <div className="font-medium">${(p.amount / 100).toFixed(2)}</div>
                    <div className="text-xs text-muted-foreground capitalize">{p.status}</div>
                  </div>
                </div>
              ))}
            </div>
          </CardContent>
        </Card>
      )}
    </div>
  );
};

export default RenewalTab;
